import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Building2, MapPin, Fuel } from 'lucide-react';

const dealerships = ['BPCL', 'HPCL', 'IOCL', 'Jio-BP', 'Nayara', 'Reliance Petroleum', 'Shell'];

function PumpProfileDialog({ open, onOpenChange }) {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (open) {
      fetchProfile();
    }
  }, [open]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/pump/profile`);
      setProfile(res.data);
    } catch (error) {
      toast.error('Failed to load pump profile');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/pump/profile`, profile);
      toast.success('Pump profile updated');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = user?.role === 'admin';

  const handleClose = () => {
    setEditing(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Pump Profile
          </DialogTitle>
        </DialogHeader>
        
        {!profile ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="pump-profile-dialog">
            {/* Basic Information */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-muted" />
                <h3 className="font-semibold">Basic Information</h3>
                {canEdit && !editing && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="ml-auto"
                    onClick={() => setEditing(true)}
                    data-testid="edit-profile-button"
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Pump Name</label>
                  <Input
                    value={profile.pump_name}
                    onChange={(e) => setProfile({ ...profile, pump_name: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Owner Name</label>
                  <Input
                    value={profile.owner_name}
                    onChange={(e) => setProfile({ ...profile, owner_name: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Dealership</label>
                  {editing ? (
                    <Select value={profile.dealership} onValueChange={(value) => setProfile({ ...profile, dealership: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dealerships.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={profile.dealership} disabled />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile</label>
                  <Input
                    value={profile.mobile}
                    onChange={(e) => setProfile({ ...profile, mobile: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    disabled={!editing}
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-muted" />
                <h3 className="font-semibold">Location</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <Input
                    value={profile.location}
                    onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Town</label>
                  <Input
                    value={profile.town}
                    onChange={(e) => setProfile({ ...profile, town: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">District</label>
                  <Input
                    value={profile.district}
                    onChange={(e) => setProfile({ ...profile, district: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <Input
                    value={profile.state}
                    onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                    disabled={!editing}
                  />
                </div>
              </div>
            </div>

            {/* Infrastructure */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Fuel className="w-4 h-4 text-muted" />
                <h3 className="font-semibold">Infrastructure</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-4 border border-petrol/20 bg-petrol/5">
                  <h4 className="text-sm font-semibold mb-3 text-petrol">MS (Petrol)</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted text-xs">Dispensers</p>
                      <p className="font-medium">{profile.ms_dispensers}</p>
                    </div>
                    <div>
                      <p className="text-muted text-xs">Tanks</p>
                      <p className="font-medium">{profile.ms_tanks}</p>
                    </div>
                    <div>
                      <p className="text-muted text-xs">Capacity</p>
                      <p className="font-medium">{profile.ms_tank_capacity?.toLocaleString()}L</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 border border-diesel/20 bg-diesel/5">
                  <h4 className="text-sm font-semibold mb-3 text-diesel">HSD (Diesel)</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted text-xs">Dispensers</p>
                      <p className="font-medium">{profile.hsd_dispensers}</p>
                    </div>
                    <div>
                      <p className="text-muted text-xs">Tanks</p>
                      <p className="font-medium">{profile.hsd_tanks}</p>
                    </div>
                    <div>
                      <p className="text-muted text-xs">Capacity</p>
                      <p className="font-medium">{profile.hsd_tank_capacity?.toLocaleString()}L</p>
                    </div>
                  </div>
                </Card>
              </div>
              <p className="text-xs text-muted mt-3">Infrastructure details cannot be changed after setup</p>
            </div>

            {editing && (
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => { setEditing(false); fetchProfile(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="save-profile-button">
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PumpProfileDialog;
